import styled, { css } from 'styled-components'
import { TabPanel } from 'components/sto/Tabs'
import { stoColor, stoMedia, stoRadius, stoShadow, stoSpace, stoType } from 'constants/stoTheme'

/** Panel/cell transition length; HeroMetrics.tsx waits this long before resetting a closed panel. */
export const TRANSITION_MS = 300
const DURATION = `${TRANSITION_MS}ms`
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'

/**
 * On desktop the card's own width decides: all cells in one row while each gets CELL_MIN_PX,
 * else pairs (2×2), else one per row. Below desktop at most pairs; below tablet one per row
 * (Figma mobile). One-column rules come after the pair rules, so they win where both match.
 */
const CELL_MIN_PX = 200
const inPairs = (columns: number, rules: string) => css`
  ${stoMedia.belowDesktop} {
    ${rules}
  }

  @container (max-width: ${columns * CELL_MIN_PX - 1}px) {
    ${rules}
  }
`
const oneColumn = (rules: string) => css`
  ${stoMedia.belowTablet} {
    ${rules}
  }

  @container (max-width: ${2 * CELL_MIN_PX - 1}px) {
    ${rules}
  }
`

export const Root = styled.div<{ $open: boolean }>`
  container-type: inline-size;
  overflow: hidden;
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  box-shadow: ${({ $open }) => ($open ? stoShadow.sm : 'none')};
  transition: box-shadow ${DURATION} ${EASING};
`

/**
 * The 1px gap over a border-coloured background draws every divider, both axes. The grid runs
 * 1px under the card's bottom border, so the closed panel's gap never doubles that border.
 */
export const Grid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: 1px;
  margin-bottom: -1px;
  background: ${stoColor.borderSubtle};

  ${({ $columns }) => inPairs($columns, `grid-template-columns: repeat(${Math.min($columns, 2)}, minmax(0, 1fr));`)}

  ${oneColumn(`grid-template-columns: minmax(0, 1fr);`)}
`

export const Cell = styled.div<{
  $active: boolean
  $dimmed: boolean
  $expandable: boolean
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: ${stoSpace[4]};

  ${oneColumn(`padding: ${stoSpace[3]};`)}
  background: ${({ $dimmed }) => ($dimmed ? stoColor.surfaceAlt : stoColor.surface)};
  box-shadow: ${({ $dimmed }) => ($dimmed ? stoShadow.xs : 'none')};
  cursor: ${({ $expandable }) => ($expandable ? 'pointer' : 'default')};
  transition: background-color ${DURATION} ${EASING};
  scroll-margin-top: 96px;

  ${stoMedia.tabletUp} {
    scroll-margin-top: 104px;
  }

  /* The open cell runs into the panel right below its row: cover the divider between them. */
  ${({ $active }) =>
    $active &&
    css`
      &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: -1px;
        z-index: 2;
        height: 1px;
        background: ${stoColor.surface};
      }
    `}
`

/**
 * Empty cell closing the last row, as Figma does, rather than stretching the last metric.
 * `$inRow` / `$inPairs`: whether that layout needs it; one per row never does.
 */
export const FillerCell = styled.div<{ $columns: number; $inRow: boolean; $inPairs: boolean }>`
  display: ${({ $inRow }) => ($inRow ? 'block' : 'none')};
  background: ${stoColor.surface};

  ${({ $columns, $inPairs }) => inPairs($columns, `display: ${$inPairs ? 'block' : 'none'};`)}

  ${oneColumn(`display: none;`)}
`

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${stoSpace[2]};
  min-width: 0;
`

export const Label = styled.span`
  ${stoType.statLabel};
  overflow: hidden;
  color: ${stoColor.textSecondary};
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** Sits above the cell-wide toggle overlay so it stays independently hoverable. */
export const LabelTooltip = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex: 0 0 auto;
`

/** One per row (Figma mobile), the chevron sits at the row's end. */
export const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${stoSpace[1]};
  min-width: 0;

  ${oneColumn(`justify-content: space-between;`)}
`

export const Value = styled.span`
  ${stoType.metricValue};
  color: ${stoColor.textPrimary};
  overflow-wrap: anywhere;
`

export const ValueLink = styled.a`
  ${stoType.metricValue};
  position: relative;
  z-index: 1;
  color: ${stoColor.textPrimary} !important;
  text-decoration: underline;
  text-underline-offset: 3px;
  overflow-wrap: anywhere;
  transition: opacity 150ms;

  /* Stays black and underlined: a lighter shade, not the link blue. */
  &:hover {
    opacity: 0.7;
  }
`

/** The chevron is the button; its ::after stretches over the whole cell. */
export const Toggle = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: none;
  color: ${stoColor.textPrimary};
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible::after {
    outline: 2px solid ${stoColor.primary};
    outline-offset: -2px;
  }
`

export const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform ${DURATION} ${EASING};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/**
 * A full-width grid item placed by `order` right after the open cell's row (`--panel-order`
 * on the full row, `--panel-order-2` in pairs, `--panel-order-1` one per row), so it opens
 * under that row.
 * The grid gaps draw its dividers. Closed, it is 0px tall and sits last.
 * Height animates without measuring: grid rows go 0fr → 1fr.
 */
export const Panel = styled.div<{ $open: boolean; $columns: number }>`
  position: relative;
  z-index: 1;
  grid-column: 1 / -1;
  order: var(--panel-order);
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  background: ${stoColor.surface};
  transition: grid-template-rows ${DURATION} ${EASING};

  ${({ $columns }) => inPairs($columns, `order: var(--panel-order-2);`)}

  ${oneColumn(`order: var(--panel-order-1);`)}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/** Fades the content, not the panel. */
export const PanelClip = styled.div<{ $open: boolean }>`
  min-height: 0;
  overflow: hidden;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity ${DURATION} ${EASING};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/** The content fades in from a blur, like tab content: on open and on switching metrics. */
export const PanelInner = styled(TabPanel)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${stoSpace[4]};
  /* The cell's own padding, so the panel's text lines up with the metric above it. */
  padding: ${stoSpace[4]};

  ${oneColumn(`padding: ${stoSpace[3]};`)}
`

export const Entries = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${stoSpace[4]} ${stoSpace[8]};
  width: 100%;
  margin: 0;

  @container (max-width: 480px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

export const Entry = styled.div`
  min-width: 0;
`

/** The metric's own label type, so the panel's labels never read bigger than the metric's. */
export const EntryTitle = styled.dt`
  ${stoType.statLabel};
  display: flex;
  align-items: center;
  gap: ${stoSpace[2]};
  color: ${stoColor.textSecondary};
`

export const EntryText = styled.dd`
  ${stoType.metricEntryText};
  margin: 0;
  color: ${stoColor.textPrimary};
  white-space: pre-wrap;

  ${stoMedia.belowDesktop} {
    font-size: 12px;
  }
`

/** A panel row's text as a link: same type as the text, underlined, black (as ValueLink). */
export const EntryLink = styled.a`
  color: ${stoColor.textPrimary} !important;
  text-decoration: underline;
  text-underline-offset: 3px;
  overflow-wrap: anywhere;
  transition: opacity 150ms;

  &:hover {
    opacity: 0.7;
  }
`

export const Note = styled.p`
  ${stoType.bodySm};
  margin: 0;
  color: ${stoColor.textSecondary};
`

/** The anchor chip: copies the link to this metric (with its tooltip), never navigates. */
export const AnchorTag = styled.span`
  ${stoType.tag};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${stoSpace[1]} ${stoSpace[2]};
  border-radius: ${stoRadius.sm};
  background: ${stoColor.surfaceAlt};
  color: ${stoColor.textSecondary};

  &:hover {
    color: ${stoColor.textPrimary};
  }
`

export const Placeholder = styled.div`
  height: 78px;
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  background: ${stoColor.surfaceAlt};
`
